import { generatePath } from '../mapUtilities';

const haveSameIds = (idsString: string, itemIdString: string): boolean => {
  if (!idsString || !itemIdString) {
    return false;
  }

  const ids = idsString.split(',');
  const itemIds = itemIdString.split(',');

  return ids.some(id => itemIds.includes(id));
};

export const resolveIsActive = (
  id: string,
  activeId: string,
  hoverId: string,
): boolean => haveSameIds(hoverId || activeId, id);

export const isGroupAllDisabled = (
  groupId: string,
  disabledIds: ReadonlySet<string>,
): boolean => {
  if (disabledIds.size === 0 || !groupId) {
    return false;
  }

  return groupId.split(',').every(id => disabledIds.has(id));
};

export const isIdDisabled = (
  id: string,
  disabledIds: ReadonlySet<string>,
): boolean => disabledIds.has(id);

const Z_LAYERS = {
  polyline: { base: 496, active: 510 },
  asset: { base: 498, active: 512 },
  workOrder: { base: 500, active: 514 },
  user: { base: 501, active: 515 },
  cluster: { base: 506, active: 516 },
} as const;

export const resolveZIndex = (
  layer: keyof typeof Z_LAYERS,
  isActive = false,
): number => Z_LAYERS[layer][isActive ? 'active' : 'base'];

export const resolveOpacity = (disabled: boolean): number =>
  disabled ? 0.5 : 1;

// Block ability to highlight map markers. Resolves rare cases that could
// arise while using the drawing tools.
export const MARKER_STYLE: React.CSSProperties = { userSelect: 'none' };

export const lineStringPath = (location: {
  type?: string;
  coordinates?: unknown;
}): google.maps.LatLngLiteral[] | null =>
  location?.type === 'LineString'
    ? generatePath(location.coordinates as [number, number][])
    : null;

type MarkerType =
  | 'cluster'
  | 'asset'
  | 'assetGroup'
  | 'workOrder'
  | 'workOrderGroup'
  | 'user';

// The [x%, y%] spot on the icon that lines up with the map coordinate.
// Centered (50%, 50%) for round icons; for pins the y is near the bottom
// so the pointed tip lands on the coordinate.
const MARKER_ANCHORS: Record<
  MarkerType,
  { base: [string, string]; active: [string, string] }
> = {
  cluster: { base: ['50%', '50%'], active: ['50%', '50%'] },
  asset: { base: ['50%', '50%'], active: ['50%', '69%'] },
  assetGroup: { base: ['50%', '50%'], active: ['50%', '86%'] },
  workOrder: { base: ['50%', '70%'], active: ['50%', '70%'] },
  workOrderGroup: { base: ['50%', '82%'], active: ['50%', '82%'] },
  user: { base: ['50%', '81%'], active: ['50%', '81%'] },
};

type MarkerAnchor = { anchorLeft: string; anchorTop: string };

// Looks up the anchor point for a marker and returns it as the CSS offsets
// AdvancedMarker expects. The values are negated because the marker is
// shifted by minus the anchor — moving that point onto the coordinate.
export const resolveAnchor = (
  type: MarkerType,
  isActive = false,
): MarkerAnchor => {
  const [x, y] = MARKER_ANCHORS[type][isActive ? 'active' : 'base'];
  return { anchorLeft: `calc(-1 * ${x})`, anchorTop: `calc(-1 * ${y})` };
};
